<!-- eslint-disable perfectionist/sort-modules -->
<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import {
  computed, onMounted, reactive, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'vue3-toastify';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import useIncentiveActivationPermission from '~/hooks/useIncentiveActivationPermission';
import { campaignManagementService } from '~/services/campaign-management-api/campaign-management-api';
import type { CampaignManagementApi } from '~/services/campaign-management-api/campaign-management-api.types';

import type { IncentiveNullableFieldState } from '../types';

import { getCachedIncentiveListItem } from '../cache/incentiveListItemCache';
import ActivateIncentiveModal from '../components/ActivateIncentiveModal.vue';
import ArchiveIncentiveModal from '../components/ArchiveIncentiveModal.vue';
import EditActiveIncentiveWarningModal from '../components/EditActiveIncentiveWarningModal.vue';
import IncentiveBannerUploader from '../components/IncentiveBannerUploader.vue';
import IncentiveStatusBadge from '../components/IncentiveStatusBadge.vue';
import {
  createInitialFieldState,
  mapIncentiveDetailToFormData,
  mapIncentiveListItemToDetail,
  mapUpdateIncentivePayload,
} from '../mappers/incentiveMappers';
import { createEmptyIncentiveFormValues, type IncentiveFormValues, incentiveSchema } from '../schemas/incentiveSchema';
import { formatIncentiveDate, parseIncentiveApiError } from '../utils';

const route = useRoute();
const router = useRouter();
const { canActivate } = useIncentiveActivationPermission();

type DetailErrorKind = 'forbidden' | 'not-found' | 'unknown';

const incentiveId = computed(() => String(route.query.id ?? ''));

const isLoading = ref(true);
const errorKind = ref<DetailErrorKind | null>(null);
const hasError = computed(() => errorKind.value !== null);
const incentive = ref<CampaignManagementApi.IncentiveDetail | null>(null);

const isEditing = ref(false);
const isSaving = ref(false);
const showEditWarning = ref(false);
const bannerUploaderRef = ref<InstanceType<typeof IncentiveBannerUploader> | null>(null);

const bannerImageUrl = ref<null | string>(null);
const isBannerLoading = ref(false);

/**
 * vee-validate governs "is this valid input" (schema validation, `errors`, submit
 * gating). It is intentionally NOT the source of truth for what gets sent on PATCH —
 * `fieldState` is a separate side-channel (unchanged from before this refactor) that
 * tracks, per nullable field, whether the user explicitly cleared it via "Limpar",
 * left it untouched, or typed a new value. `mapUpdateIncentivePayload` reconciles
 * both into the omit/null/value payload; zod never encodes that tri-state itself.
 */
const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
  setFieldError,
} = useForm<IncentiveFormValues>({
  initialValues: createEmptyIncentiveFormValues(),
  validationSchema: toTypedSchema(incentiveSchema),
});

const fieldOptions = {
  validateOnBlur: true,
  validateOnChange: false,
  validateOnInput: false,
  validateOnModelUpdate: false,
};

const [name, nameProps] = defineField('name', fieldOptions);
const [titlePtbr, titlePtbrProps] = defineField('title_ptbr', fieldOptions);
const [descriptionPtbr, descriptionPtbrProps] = defineField('description_ptbr', fieldOptions);
const [businessRules, businessRulesProps] = defineField('business_rules', fieldOptions);
const [triggerDescription, triggerDescriptionProps] = defineField('trigger_description', fieldOptions);
const [acceptanceCriteria, acceptanceCriteriaProps] = defineField('acceptance_criteria', fieldOptions);
const [bannerFileExternalId] = defineField('banner_file_external_id', fieldOptions);

const nullableFieldRefs = {
  acceptance_criteria: acceptanceCriteria,
  banner_file_external_id: bannerFileExternalId,
  business_rules: businessRules,
  trigger_description: triggerDescription,
} as const;

const fieldState = reactive<IncentiveNullableFieldState>(createInitialFieldState());

const showArchiveModal = ref(false);
const isArchiving = ref(false);

const showActivateModal = ref(false);
const isActivating = ref(false);

const canEdit = computed(() => incentive.value?.status !== 'ARCHIVED');
const canArchive = computed(() => incentive.value?.status !== 'ARCHIVED');
const canShowActivate = computed(() => (
  incentive.value?.status === 'UNDER_ANALYSIS' && canActivate.value
));

async function loadBannerImage(bannerId: null | string | undefined) {
  if (!bannerId) {
    bannerImageUrl.value = null;
    return;
  }

  isBannerLoading.value = true;

  try {
    const banner = await campaignManagementService.getIncentiveBanner(bannerId);
    bannerImageUrl.value = banner.url;
  } catch {
    bannerImageUrl.value = null;
  } finally {
    isBannerLoading.value = false;
  }
}

/**
 * The list endpoint (v2) returns the full CampaignIncentive field set per item, so the
 * cached list item alone is enough to render this page — no follow-up GET /incentives/{id}
 * call. Navigating here without coming from the list (direct URL, refresh) has nothing to
 * read, so it lands on "not-found".
 */
function loadIncentive() {
  isLoading.value = true;
  errorKind.value = null;
  isEditing.value = false;

  const cached = incentiveId.value ? getCachedIncentiveListItem(incentiveId.value) : null;

  if (cached) {
    incentive.value = mapIncentiveListItemToDetail(cached);
  } else {
    incentive.value = null;
    errorKind.value = 'not-found';
  }

  isLoading.value = false;
  loadBannerImage(incentive.value?.banner_file_external_id);
}

function startEdit() {
  if (!incentive.value) return;

  resetForm({ values: mapIncentiveDetailToFormData(incentive.value) });
  Object.assign(fieldState, createInitialFieldState());
  isEditing.value = true;
}

function onEditClick() {
  if (!incentive.value || !canEdit.value) return;

  if (incentive.value.status === 'ACTIVE') {
    showEditWarning.value = true;
    return;
  }

  startEdit();
}

function onConfirmEditWarning() {
  showEditWarning.value = false;
  startEdit();
}

function onCancelEditWarning() {
  showEditWarning.value = false;
}

function cancelEdit() {
  isEditing.value = false;
  resetForm();
}

function onFieldInput(field: keyof IncentiveNullableFieldState) {
  fieldState[field] = 'value';
}

function onFieldClear(field: keyof IncentiveNullableFieldState) {
  nullableFieldRefs[field].value = null;
  fieldState[field] = 'cleared';
}

function onBannerChange(value: null | string) {
  bannerFileExternalId.value = value;
  if (value) fieldState.banner_file_external_id = 'value';
}

function onBannerCleared() {
  fieldState.banner_file_external_id = 'cleared';
}

const saveIncentive = handleSubmit(async (values) => {
  if (!incentive.value) return;

  isSaving.value = true;

  try {
    let uploadedBannerId: null | string;

    try {
      uploadedBannerId = (await bannerUploaderRef.value?.upload()) ?? null;
    } catch {
      // The uploader already surfaced its own error toast — stop here without
      // saving the incentive with a stale/missing banner.
      return;
    }

    const finalValues = { ...values, banner_file_external_id: uploadedBannerId };
    const payload = mapUpdateIncentivePayload(finalValues, incentive.value, fieldState);
    incentive.value = await campaignManagementService.updateIncentive(incentiveId.value, payload);
    isEditing.value = false;
    toast.success('Incentivo atualizado com sucesso!');
    router.push('/incentives');
  } catch (error) {
    const parsed = parseIncentiveApiError(error);

    if (parsed.kind === 'validation' && parsed.fieldErrors) {
      Object.entries(parsed.fieldErrors).forEach(([field, messages]) => {
        setFieldError(field as keyof IncentiveFormValues, messages[0]);
      });
    }

    toast.error(parsed.message);
  } finally {
    isSaving.value = false;
  }
}, () => {
  toast.error('Corrija os erros antes de salvar.');
});

function openArchiveModal() {
  showArchiveModal.value = true;
}

function closeArchiveModal() {
  showArchiveModal.value = false;
}

async function confirmArchive() {
  isArchiving.value = true;

  try {
    incentive.value = await campaignManagementService.archiveIncentive(incentiveId.value);
    showArchiveModal.value = false;
    toast.success('Incentivo arquivado com sucesso.');
    router.push('/incentives');
  } catch (error) {
    toast.error(parseIncentiveApiError(error).message);
  } finally {
    isArchiving.value = false;
  }
}

function openActivateModal() {
  showActivateModal.value = true;
}

function closeActivateModal() {
  showActivateModal.value = false;
}

async function confirmActivate() {
  isActivating.value = true;

  try {
    incentive.value = await campaignManagementService.activateIncentive(incentiveId.value);
    showActivateModal.value = false;
    toast.success('Incentivo ativado com sucesso.');
    router.push('/incentives');
  } catch (error) {
    toast.error(parseIncentiveApiError(error).message);
  } finally {
    isActivating.value = false;
  }
}

watch(incentiveId, () => {
  loadIncentive();
});

onMounted(() => {
  document.title = 'Detalhe do Incentivo';
  loadIncentive();
});
</script>

<template>
  <DashboardLayout>
    <template #header-left>
      <RouterLink to="/incentives" class="detail-breadcrumb">
        ← Incentivos
      </RouterLink>
      <h2 class="page-title" style="margin-top: 6px;">
        <template v-if="isLoading">
          <span class="placeholder-glow">
            <span class="placeholder col-5" style="height: 22px; display: inline-block;" />
          </span>
        </template>
        <template v-else-if="incentive">{{ incentive.name }}</template>
        <template v-else-if="errorKind === 'forbidden'">Acesso não permitido</template>
        <template v-else-if="errorKind === 'not-found'">Incentivo não encontrado</template>
        <template v-else>Não foi possível carregar o incentivo</template>
      </h2>
      <p v-if="!isLoading && incentive" class="page-subtitle">
        criado por {{ incentive.created_by }} em {{ formatIncentiveDate(incentive.created_at) }}
        <template v-if="incentive.actived_at">
          · ativado por {{ incentive.actived_by ?? '—' }} em {{ formatIncentiveDate(incentive.actived_at) }}
        </template>
      </p>
    </template>

    <template #actions>
      <div v-if="incentive && !isLoading" class="d-flex align-items-center gap-2">
        <IncentiveStatusBadge v-if="!isEditing" size="md" :status="incentive.status" />

        <template v-if="!isEditing">
          <button
            v-if="canShowActivate"
            type="button"
            class="btn btn-primary"
            @click="openActivateModal"
          >
            Ativar
          </button>
          <button
            v-if="canEdit"
            type="button"
            class="btn btn-outline-secondary"
            @click="onEditClick"
          >
            Editar
          </button>
          <button
            v-if="canArchive"
            type="button"
            class="btn btn-outline-danger"
            @click="openArchiveModal"
          >
            Arquivar
          </button>
        </template>
        <template v-else>
          <button
            type="button"
            class="btn btn-ghost-secondary"
            :disabled="isSaving"
            @click="cancelEdit"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="isSaving"
            @click="saveIncentive"
          >
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" />
            Salvar
          </button>
        </template>
      </div>
    </template>

    <div v-if="hasError && !isLoading" class="row row-cards">
      <div class="col-12">
        <div class="error-state">
          <template v-if="errorKind === 'forbidden'">
            Você não tem permissão para acessar este incentivo.
          </template>
          <template v-else-if="errorKind === 'not-found'">
            Incentivo não encontrado.
          </template>
          <template v-else>
            Não foi possível carregar os dados do incentivo.
          </template>
        </div>
      </div>
    </div>

    <div v-else-if="incentive" class="row row-cards">
      <div class="col-12">
        <div class="card">
          <template v-if="!isEditing">
            <div class="fs-section">
              <div class="fs-header">
                <div class="fs-num">1</div>
                <h4 class="fs-title">Identificação</h4>
              </div>
              <p class="fs-sub">Como este incentivo aparece na listagem e para o parceiro.</p>

              <div class="mb-3">
                <div class="detail-label">Título</div>
                <div class="detail-value">{{ incentive.title_ptbr || '—' }}</div>
              </div>
              <div class="mb-3">
                <div class="detail-label">Descrição</div>
                <div class="detail-value">{{ incentive.description_ptbr || '—' }}</div>
              </div>
            </div>

            <div class="fs-section">
              <div class="fs-header">
                <div class="fs-num">2</div>
                <h4 class="fs-title">Regras e critérios</h4>
              </div>
              <p class="fs-sub">Detalhes usados para configurar o gatilho e validar a elegibilidade.</p>

              <div class="mb-3">
                <div class="detail-label">Regras de negócio</div>
                <div class="detail-value">{{ incentive.business_rules ?? '—' }}</div>
              </div>
              <div class="mb-3">
                <div class="detail-label">Descrição do gatilho</div>
                <div class="detail-value">{{ incentive.trigger_description ?? '—' }}</div>
              </div>
              <div class="mb-3">
                <div class="detail-label">Critérios de aceite</div>
                <div class="detail-value">{{ incentive.acceptance_criteria ?? '—' }}</div>
              </div>
            </div>

            <div class="fs-section">
              <div class="fs-header">
                <div class="fs-num">3</div>
                <h4 class="fs-title">Banner</h4>
              </div>
              <p class="fs-sub">Imagem exibida para o parceiro quando o incentivo estiver ativo.</p>

              <div class="mb-3">
                <div class="detail-label">Banner</div>
                <img
                  v-if="bannerImageUrl"
                  :src="bannerImageUrl"
                  alt="Banner do incentivo"
                  class="detail-banner-image"
                />
                <div v-else-if="isBannerLoading" class="detail-value">Carregando…</div>
                <div v-else class="detail-value">—</div>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="fs-section">
              <div class="fs-header">
                <div class="fs-num">1</div>
                <h4 class="fs-title">Identificação</h4>
              </div>
              <p class="fs-sub">Como este incentivo aparece na listagem e para o parceiro.</p>

              <div class="row g-3">
                <div class="col-12">
                  <label class="fs-label" for="edit-inc-name">
                    Nome <span class="fs-req">*</span>
                  </label>
                  <input
                    id="edit-inc-name"
                    v-model="name"
                    v-bind="nameProps"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.name }"
                  />
                  <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                  <small v-else class="fs-hint">Nome interno usado para identificar o incentivo nas campanhas.</small>
                </div>

                <div class="col-12">
                  <label class="fs-label" for="edit-inc-title">
                    Título <span class="fs-req">*</span>
                  </label>
                  <input
                    id="edit-inc-title"
                    v-model="titlePtbr"
                    v-bind="titlePtbrProps"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.title_ptbr }"
                  />
                  <div v-if="errors.title_ptbr" class="invalid-feedback">{{ errors.title_ptbr }}</div>
                  <small v-else class="fs-hint">Título exibido para o parceiro.</small>
                </div>

                <div class="col-12">
                  <label class="fs-label" for="edit-inc-description">
                    Descrição <span class="fs-req">*</span>
                  </label>
                  <textarea
                    id="edit-inc-description"
                    v-model="descriptionPtbr"
                    v-bind="descriptionPtbrProps"
                    class="form-control"
                    :class="{ 'is-invalid': errors.description_ptbr }"
                    rows="3"
                  />
                  <div v-if="errors.description_ptbr" class="invalid-feedback">
                    {{ errors.description_ptbr }}
                  </div>
                  <small v-else class="fs-hint">Aparece na listagem de incentivos.</small>
                </div>
              </div>
            </div>

            <div class="fs-section">
              <div class="fs-header">
                <div class="fs-num">2</div>
                <h4 class="fs-title">Regras e critérios</h4>
              </div>
              <p class="fs-sub">Detalhes usados para configurar o gatilho e validar a elegibilidade.</p>

              <div class="row g-3">
                <div class="col-12">
                  <div class="fs-label-row">
                    <label class="fs-label mb-0" for="edit-inc-business-rules">Regras de negócio</label>
                    <button type="button" class="btn btn-link btn-sm p-0" @click="onFieldClear('business_rules')">
                      Limpar
                    </button>
                  </div>
                  <textarea
                    id="edit-inc-business-rules"
                    v-model="businessRules"
                    v-bind="businessRulesProps"
                    class="form-control"
                    rows="3"
                    @input="onFieldInput('business_rules')"
                  />
                  <small class="fs-hint">
                    Opcional. Detalhe as regras para quem for configurar ou auditar o incentivo.
                  </small>
                </div>

                <div class="col-12">
                  <div class="fs-label-row">
                    <label class="fs-label mb-0" for="edit-inc-trigger">Descrição do gatilho</label>
                    <button type="button" class="btn btn-link btn-sm p-0" @click="onFieldClear('trigger_description')">
                      Limpar
                    </button>
                  </div>
                  <textarea
                    id="edit-inc-trigger"
                    v-model="triggerDescription"
                    v-bind="triggerDescriptionProps"
                    class="form-control"
                    rows="3"
                    @input="onFieldInput('trigger_description')"
                  />
                  <small class="fs-hint">Opcional. Explique o evento que aciona o incentivo.</small>
                </div>

                <div class="col-12">
                  <div class="fs-label-row">
                    <label class="fs-label mb-0" for="edit-inc-acceptance">Critérios de aceite</label>
                    <button type="button" class="btn btn-link btn-sm p-0" @click="onFieldClear('acceptance_criteria')">
                      Limpar
                    </button>
                  </div>
                  <textarea
                    id="edit-inc-acceptance"
                    v-model="acceptanceCriteria"
                    v-bind="acceptanceCriteriaProps"
                    class="form-control"
                    rows="3"
                    @input="onFieldInput('acceptance_criteria')"
                  />
                  <small class="fs-hint">Opcional. Usado para validar antes de conceder o incentivo.</small>
                </div>
              </div>
            </div>

            <div class="fs-section">
              <div class="fs-header">
                <div class="fs-num">3</div>
                <h4 class="fs-title">Banner</h4>
              </div>
              <p class="fs-sub">Imagem exibida para o parceiro quando o incentivo estiver ativo.</p>

              <div class="row g-3">
                <div class="col-12">
                  <IncentiveBannerUploader
                    ref="bannerUploaderRef"
                    :model-value="bannerFileExternalId"
                    @update:model-value="onBannerChange"
                    @cleared="onBannerCleared"
                  />
                  <small class="fs-hint">
                    Opcional. O arquivo só é enviado ao clicar em "Salvar".
                  </small>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <EditActiveIncentiveWarningModal
      :show="showEditWarning"
      @confirm="onConfirmEditWarning"
      @cancel="onCancelEditWarning"
    />

    <ArchiveIncentiveModal
      :show="showArchiveModal"
      :loading="isArchiving"
      @confirm="confirmArchive"
      @cancel="closeArchiveModal"
    />

    <ActivateIncentiveModal
      :show="showActivateModal"
      :loading="isActivating"
      @confirm="confirmActivate"
      @cancel="closeActivateModal"
    />
  </DashboardLayout>
</template>

<style scoped lang="scss">
.detail-breadcrumb {
  display: inline-block;
  font-size: 12px;
  color: #00B569;
  font-weight: 600;
  margin-bottom: 2px;
  text-decoration: none;

  &:hover { text-decoration: underline; }
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #111;
  margin: 0;
}

.page-subtitle {
  font-size: 13px;
  color: #6B7280;
  margin-top: 4px;
  margin-bottom: 0;
}

.error-state {
  background: #fff;
  border: 1px solid #E4E6EB;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  color: #6B7280;
  font-size: 14px;
}

.detail-label {
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 14px;
  color: #222;
  white-space: pre-wrap;
}

.detail-banner-image {
  max-width: 320px;
  border-radius: 8px;
  border: 1px solid #e4e6eb;
  display: block;
}

.fs-section {
  padding: 24px;
  border-bottom: 1px solid #e4e6eb;

  &:last-of-type {
    border-bottom: none;
  }
}

.fs-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.fs-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00b569;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
}

.fs-title {
  font-size: 15px;
  font-weight: 700;
  color: #222222;
  margin: 0;
}

.fs-sub {
  font-size: 12.5px;
  color: #6b7280;
  margin-left: 36px;
  margin-bottom: 16px;
  line-height: 1.4;
}

.fs-label {
  font-size: 12px;
  font-weight: 600;
  color: #222222;
  margin-bottom: 4px;
  display: block;
}

.fs-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.fs-req {
  color: #ef4444;
}

.fs-hint {
  font-size: 11.5px;
  color: #6b7280;
  line-height: 1.4;
  display: block;
  margin-top: 4px;
}

.form-control {
  padding: 9px 12px;
  border-color: #e4e6eb;
  border-radius: 6px;
  font-size: 13px;

  &:focus {
    border-color: #00b569;
    box-shadow: 0 0 0 3px rgba(0, 181, 105, 0.1);
  }

  &:disabled {
    background: #f5f6f8;
    color: #9ca3af;
  }
}
</style>
